import "./index.css";
import "./i18n/config.ts";

import {
  QueryClient,
  QueryCache,
  QueryClientProvider,
  MutationCache,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import React from "react";
import { createRoot } from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";
import { RouterProvider } from "react-router-dom";

import { FallbackErrorUI } from "./components";
import { router } from "./routes";

const queryCache = new QueryCache({
  onError: (error) => {
    const errorObj = error as any;
    const errorMessage =
      errorObj?.message || errorObj?.errorDetail || "Unknown React Query error";
    console.error(`React Query Error: ${errorMessage}`);
  },
});

const mutationCache = new MutationCache({
  onError: (error) => {
    const errorObj = error as any;
    const errorMessage =
      errorObj?.message ||
      errorObj?.errorDetail ||
      "Unknown React Query mutation error";
    console.error(`React Query Mutation Error: ${errorMessage}`);
  },
});

const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      // Reduce refetch frequency to minimize API calls
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: (failureCount, error: any) => {
        // Retry on network errors, not on 4xx errors
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchOnMount: false,
    },
    mutations: {
      retry: 1,
      retryDelay: 1000,
    },
  },
});

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ErrorBoundary FallbackComponent={() => <FallbackErrorUI />}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        {process.env.NODE_ENV === "development" && <ReactQueryDevtools />}
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
